import React, { useState, useEffect } from "react";
import {
  Box, Alert, Collapse, Typography, useMediaQuery, useTheme
} from "@mui/material";
import { useAuth } from "../../../../context/AuthContext";
import { useLocation, Link } from "react-router-dom";
import { useSystems } from "../../../../context/SystemContext";
import { Users, Building, GraduationCap, Briefcase, Building2, Wrench } from "lucide-react";
import ProPlanModal from "../../../components/pro/ProPlanModal";
import DashboardLayout from "../../../usuarios/components/DashboardLayout";
import { SidebarRightWidgets } from "./SidebarRightWidgets";
import SocialManager from "../post/SocialManager";
import PeopleYouMayKnow from "./widgets/PeopleYouMayKnow";
import ActiveCompanies from "./widgets/ActiveCompanies";
import ProfessionalProfilesCard from "./widgets/ProfessionalProfilesCard";
import RecommendedCourses from "./widgets/RecommendedCourses";
import PeopleNetworkPage from "./PeopleNetworkPage";

const CommunityWall = ({ isPersistent = false }) => {
  const { user: loggedInUser, loading: authLoading } = useAuth();
  const { isSidebarCollapsed, activeProfile } = useSystems();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [isProModalOpen, setIsProModalOpen] = useState(false);
  const [trialVencido, setTrialVencido] = useState(false);
  const [proActivo, setProActivo] = useState(false);
  const theme = useTheme();
  const isSmallScreen = !useMediaQuery(theme.breakpoints.up("lg"));

  // Consultar estado Pro (TRIALS_API) para aplicar bloqueo de contenido Pro en comunidad
  useEffect(() => {
    const uid = loggedInUser?.user_id || loggedInUser?.id || 1500;
    console.log("[CommunityWall] Verificando Pro. UID:", uid);
    fetch("https://apiweb.hitpoly.com/ajax/trialsController.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accion: "estado", usuario_id: uid })
    })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        console.log("[CommunityWall] Respuesta API:", data);
        const rawLocal = localStorage.getItem("hitpoly_trial");
        console.log("[CommunityWall] localStorage hitpoly_trial:", rawLocal);
        // Si la API tiene datos contradictorios (activo: false pero fin futuro), confiar en fin
        const finFuture = data && data.fin && new Date(data.fin).getTime() > Date.now();
        const activoReal = (data && data.status === "success" && (data.activo || finFuture));
        if (activoReal) {
          console.log("[CommunityWall] Pro ACTIVO (API o fin futuro):", data);
          setProActivo(true);
          setTrialVencido(false);
        } else if (data && data.status === "success" && !data.activo && !finFuture) {
          console.log("[CommunityWall] Pro INACTIVO (API). Verificando si hay prueba vencida local...");
          try {
            const raw = localStorage.getItem("hitpoly_trial");
            if (raw) {
              const t = JSON.parse(raw);
              if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                console.log("[CommunityWall] Hay prueba vencida en localStorage.");
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setTrialVencido(true);
              } else {
                console.log("[CommunityWall] No hay prueba vencida en localStorage. Nunca tuvo prueba o fue limpiada.");
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setTrialVencido(false);
              }
            } else {
              console.log("[CommunityWall] No hay prueba en localStorage pero API tiene fin.");
              setProActivo(false);
              setTrialVencido(!!(data && data.fin));
            }
          } catch (e) {
            console.log("[CommunityWall] Error leyendo localStorage:", e);
            setProActivo(false);
            setTrialVencido(false);
          }
        } else {
          console.log("[CommunityWall] API no devolvió success. Datos:", data);
          // Si hay error en API, confiar en localStorage
          try {
            const raw = localStorage.getItem("hitpoly_trial");
            if (raw) {
              const t = JSON.parse(raw);
              if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
                setProActivo(true);
                setTrialVencido(false);
              } else {
                localStorage.removeItem("hitpoly_trial");
                setProActivo(false);
                setTrialVencido(!!raw);
              }
            } else {
              setProActivo(false);
              setTrialVencido(false);
            }
          } catch (e) {
            setProActivo(false);
            setTrialVencido(false);
          }
        }
      })
      .catch((err) => {
        console.log("[CommunityWall] Error en fetch de Pro:", err);
        try {
          const raw = localStorage.getItem("hitpoly_trial");
          if (raw) {
            const t = JSON.parse(raw);
            if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
              setProActivo(true);
              setTrialVencido(false);
            } else {
              localStorage.removeItem("hitpoly_trial");
              setProActivo(false);
              setTrialVencido(!!raw);
            }
          } else {
            setProActivo(false);
            setTrialVencido(false);
          }
        } catch (e) {
          setProActivo(false);
          setTrialVencido(false);
        }
      });
  }, [loggedInUser?.user_id, loggedInUser?.id]);

  const isNetworkView = location.pathname.endsWith("/red");
  const highlightPostId = new URLSearchParams(location.search).get("postId") || null;

  const showAlert = (message, severity = "success") => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const profileType = activeProfile?.type;
  const showPersonas = profileType === 'main';
  const showEmpresas = profileType !== 'main';
  const showProfesionales = profileType === 'enterprise';
  const showVacantes = profileType === 'professional';
  const showCursosIzquierda = profileType === 'main' || profileType === 'professional';

  if (authLoading) {
    return null;
  }

  const sideSx = {
    overflowY: "auto",
    overflowX: "auto",
    scrollbarWidth: "thin",
    msOverflowStyle: "none",
    '&::-webkit-scrollbar': { width: 0, display: 'none' },
    '&:hover': { scrollbarWidth: "thin", scrollbarColor: "#0B8DB5 transparent" },
    '&:hover::-webkit-scrollbar': { width: 4, height: 4, display: 'block' },
    '&:hover::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 3, '&:hover': { bgcolor: '#0B8DB5' } },
    '&:hover::-webkit-scrollbar-track': { bgcolor: 'transparent' },
  };

  const Content = (
    <Box sx={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
      {isSmallScreen && (
        <Box sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          py: 0.6,
          px: 1,
        }}>
          {showPersonas && (
            <Link to="/red-personas" style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40 }}>
              <Users size={20} />
              <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Personas</Typography>
            </Link>
          )}
          {showEmpresas && (
            <Link to="/red-empresas" style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40 }}>
              <Building size={20} />
              <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Empresas</Typography>
            </Link>
          )}
          <Link to="/systems/academia/cursos/Todas?categoria=Todas" style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40 }}>
            <GraduationCap size={20} />
            <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Cursos</Typography>
          </Link>
          {showVacantes && (
            <Link
              to={!proActivo ? undefined : `/systems/bolsa/vacantes${loggedInUser?.id ? `?userId=${loggedInUser.id}` : ''}`}
              onClick={() => {
                if (!proActivo) {
                  setIsProModalOpen(true);
                  return;
                }
              }}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40, cursor: !proActivo ? "pointer" : "pointer" }}
            >
              <Briefcase size={20} />
              <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Vacantes</Typography>
            </Link>
          )}
          <Link to="/servicios-empresas" style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40 }}>
            <Building2 size={20} />
            <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Srv. Empresa</Typography>
          </Link>
          <Link to="/servicios-profesionales" style={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none", color: "#555", gap: 0.5, minWidth: 40 }}>
            <Wrench size={20} />
            <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 600, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>Srv. Prof.</Typography>
          </Link>
        </Box>
      )}
      {isNetworkView ? (
        <PeopleNetworkPage onBack={() => window.history.back()} />
      ) : (
        <Box sx={{ pt: "30px", display: "flex", justifyContent: "center", px: { md: 1 }, gap: { md: isSidebarCollapsed ? "40px" : "20px" }, minHeight: "100vh", alignItems: "flex-start", boxSizing: "border-box", transition: "gap 0.3s ease" }}>
          {/* Sidebar izquierdo - solo en xl+ (3 columnas) */}
          <Box sx={{ width: { xs: "100%", xl: 360 }, flexShrink: 0, display: { xs: "none", md: "none", lg: "none", xl: "flex" }, position: "sticky", top: "30px", alignSelf: "flex-start", height: "calc(100vh - 120px)", pr: "5px", flexDirection: "column", gap: 3, '& > :last-child': { mb: '0 !important' }, ...sideSx }}>
            {showCursosIzquierda && <RecommendedCourses proActivo={proActivo} />}
            {showPersonas && <PeopleYouMayKnow />}
            {showProfesionales && <ProfessionalProfilesCard currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
            {showEmpresas && <ActiveCompanies currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
          </Box>

          {/* Feed principal */}
          <Box sx={{ flex: 1, maxWidth: "600px", pb: 5, px: { xs: 2, md: 0 }, minWidth: { lg: "500px", xs: 0 } }}>
            <SocialManager mode="feed" userId={loggedInUser?.user_id || loggedInUser?.id} highlightPostId={highlightPostId} />
            {/* Widgets para móvil */}
            <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 3, mt: 3 }}>
              {showCursosIzquierda && <RecommendedCourses />}
              {showPersonas && <PeopleYouMayKnow />}
              {showProfesionales && <ProfessionalProfilesCard currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
              {showEmpresas && <ActiveCompanies currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
              <SidebarRightWidgets />
            </Box>
          </Box>

          {/* Sidebar derecho - lg (2 columnas), xl+ (3 columnas) */}
          <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0, display: { xs: "none", lg: "flex" }, position: "sticky", top: "30px", alignSelf: "flex-start", height: "calc(100vh - 120px)", pl: "5px", flexDirection: "column", gap: 3, '& > :last-child': { mb: '0 !important' }, ...sideSx }}>
            {/* Estos en lg (2 columnas), en xl+ ya están en la izquierda */}
            <Box sx={{ display: { lg: "flex", xl: "none" }, flexDirection: "column", gap: 3 }}>
              {showCursosIzquierda && <RecommendedCourses />}
              {showPersonas && <PeopleYouMayKnow />}
              {showProfesionales && <ProfessionalProfilesCard currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
              {showEmpresas && <ActiveCompanies currentUserId={loggedInUser?.user_id || loggedInUser?.id} />}
            </Box>
            <SidebarRightWidgets />
          </Box>
        </Box>
      )}

      <ProPlanModal vencido={trialVencido}
        open={isProModalOpen}
        onClose={() => setIsProModalOpen(false)}
        herramienta="Bolsa de Trabajo"
      />
      {/* Sistema de Alertas Flotantes */}
      <Collapse in={!!alert} sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
        {alert && (
          <Alert severity={alert.severity} variant="filled" onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}
      </Collapse>
    </Box>
  );

  // Si es persistente, devolvemos el contenido directo (porque ya viene envuelto en un DashboardLayout global)
  if (isPersistent) return Content;

  return (
    <>
      <style>{`
        .community-wall-scroll::-webkit-scrollbar { height: 4px !important; width: 4px !important; display: block !important; }
        .community-wall-scroll::-webkit-scrollbar-thumb { background-color: #0B8DB5 !important; border-radius: 3px !important; }
        .community-wall-scroll::-webkit-scrollbar-track { background: transparent !important; }
      `}</style>
    <DashboardLayout disablePadding={true}>
      {Content}
    </DashboardLayout>
    </>
  );
};

export default CommunityWall;
